"use client";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { forgotPasswordSchema } from "../../../schemas";
import { OnboardingFormContainer } from "../../../components/onboarding-form-container";
import { ResendEmailLayout } from "@/app/(onboarding-layout)/components/resend-email-layout";
import { useSafeReturnTo } from "@/hooks/use-safe-return-to";
import { useForgotPassword } from "../hooks";
import { useState } from "react";

export const ForgotPasswordForm = () => {
  const [isSuccess, setIsSuccess] = useState(false);
  const { returnToParam } = useSafeReturnTo();

  const form = useForm<z.infer<typeof forgotPasswordSchema>>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const {
    mutate: forgetPassword,
    isPending,
    error,
  } = useForgotPassword({
    redirectTo: `${process.env.JURNI_APP_URL}/login/reset-password${returnToParam}`,
    onSuccess: () => {
      setIsSuccess(true);
    },
  });

  const onSubmit = async (values: z.infer<typeof forgotPasswordSchema>) => {
    forgetPassword({ email: values.email });
  };

  if (isSuccess) {
    return (
      <ResendEmailLayout
        title="Check your email"
        subtitle={
          <span>
            We&apos;ve sent you an email with instructions to reset your
            password.
            <br /> If you don&apos;t see it shortly, please check your spam
            folder.
          </span>
        }
        onResendEmail={() => onSubmit(form.getValues())}
      />
    );
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full"
      >
        <OnboardingFormContainer
          title="Forgot password?"
          subtitle="No problem. Simply enter your account's email address and we'll send you a link to reset your password."
          isError={!!error}
          errorMessage={error?.message}
          buttonLabel="Send reset link"
          buttonType="submit"
          isButtonDisabled={isPending}
          isButtonPending={isPending}
        >
          <div className="flex w-full flex-col space-y-5">
            <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email address</FormLabel>
                <FormControl>
                  <Input
                    error={!!form.formState.errors.email}
                    placeholder="Enter email address"
                    {...field}
                    value={field.value ?? ""}
                    containerClassName="w-full"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          </div>
        </OnboardingFormContainer>
      </form>
    </Form>
  );
};
